import type { ConvertedPiece } from "@kensio/pinyinjs";

import { wireCopyButton } from "./copy-button.js";
import { plainFlavours } from "./copy-html.js";
import { slot } from "./dom.js";
import { systemById, transcribe } from "./transcriptions.js";

/**
 * The panels below the pinyin: the same conversion written another way.
 *
 * A panel names its system with `data-cb-transcription`, so which systems a
 * page offers and in what order is settled by the template rather than here. A
 * panel naming a system this does not know is left alone rather than emptied,
 * which is what makes adding one to the markup a safe thing to do before the
 * code knows about it.
 */

/**
 * Give each panel's copy button the text that panel is showing.
 *
 * Plain text rather than coloured: bopomofo, Wade-Giles and the rest are not
 * tone-coloured on screen either, because each of them already writes the tone
 * — spelled into the syllable, as a separate mark, or as a digit — and
 * colouring it as well would say the same thing twice.
 */
export function wireTranscriptionCopies(panels: readonly HTMLElement[]): void {
  for (const panel of panels) {
    const button = slot(panel, "copy");
    const output = slot(panel, "output");

    if (!(button instanceof HTMLButtonElement) || output === undefined) {
      continue;
    }

    wireCopyButton(button, () => {
      const written = output.textContent;

      return written === "" ? undefined : plainFlavours(written);
    });
  }
}

/**
 * Write a conversion into every panel that names a system.
 */
export function renderTranscriptions(
  panels: readonly HTMLElement[],
  pieces: readonly ConvertedPiece[],
): void {
  for (const panel of panels) {
    const system = systemById(panel.dataset["cbTranscription"] ?? "");
    const output = slot(panel, "output");

    if (system === undefined || output === undefined) {
      continue;
    }

    output.textContent = pieces.length === 0 ? "" : transcribe(pieces, system);
  }
}
